import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useCallback, useLayoutEffect, useRef } from 'react';
import { InputNumber as AntdInputNumber, Progress as AntdProgress, Rate as AntdRate, Segmented as AntdSegmented, Switch as AntdSwitch, } from 'antd';
import { toEnglishDigits, toPersianDigits } from '@bookora/shared';
import { Icon } from '../icons/icon.js';
/**
 * ── THE STEPPER'S TWO CHEVRONS ──────────────────────────────────────────────
 *
 * `controls` is the seam: `boolean | { upIcon, downIcon }`. AntD reads
 * `controlsTemp = typeof controls === 'boolean' ? controls : undefined`, so an
 * object means "controls on, with these glyphs" — passing one does not change
 * whether the handles render, only what they draw.
 *
 * `chevron-up` / `chevron-down` and not `chevron-start`/`-end`: the stepper is
 * a vertical pair in every direction, and the registry's vertical names carry
 * no direction by design.
 *
 * `controls={false}` is left exactly as it is — a call site that turned the
 * handles off must not get them back from an icon wrapper.
 */
const STEPPER_ICONS = {
    upIcon: _jsx(Icon, { name: "chevron-up", size: 16 }),
    downIcon: _jsx(Icon, { name: "chevron-down", size: 16 }),
};
function withRegistryStepperIcons(controls) {
    if (controls === false)
        return controls;
    if (controls === undefined || controls === true)
        return STEPPER_ICONS;
    return {
        upIcon: controls.upIcon === undefined ? STEPPER_ICONS.upIcon : controls.upIcon,
        downIcon: controls.downIcon === undefined ? STEPPER_ICONS.downIcon : controls.downIcon,
    };
}
/**
 * `rc-input-number`'s ref is `HTMLInputElement & { nativeElement }`. Naming that
 * shape here would put `rc-input-number` into the emitted `.d.ts`, so the
 * wrapper takes the input element — which is what a caller actually reaches for
 * — and the handle is passed through unexamined.
 */
/**
 * Persian digits in, Persian digits out.
 *
 * AntD prints the raw value, so every duration, percentage and count field in
 * the panels showed «30» beside a `MoneyInput` showing «۳۰۰٬۰۰۰». The display
 * swaps digits one-for-one (so the caret never jumps) and the decimal point for
 * «٫»; the parser accepts Persian, Arabic-Indic and ASCII digits alike, so a
 * Persian keyboard can type into the field. Only a default: a call site that
 * passes its own `formatter` or `parser` keeps both halves of its own.
 */
const persianNumberFormatter = (value, info) => {
    const raw = info.userTyping
        ? info.input
        : value === undefined || value === null
            ? ''
            : String(value);
    return toPersianDigits(raw).replace('.', '٫');
};
const persianNumberParser = (display) => toEnglishDigits(display ?? '')
    .replace('٫', '.')
    .replace(/[^\d.-]/g, '');
/**
 * ── THE STEPPER'S ENGLISH NAMES ─────────────────────────────────────────────
 *
 * rc-input-number hard-codes `aria-label="Increase Value"` / `"Decrease
 * Value"` on the two handles (`StepHandler.js`), with no prop to change it, so
 * a screen reader announced English on every quantity, price and duration
 * field. The labels are rewritten on the rendered nodes after each commit.
 * React never touches them again unless it remounts a handle (the prop it
 * would write is a constant), and a remount re-renders this wrapper, which
 * rewrites them again.
 */
const STEPPER_LABELS = {
    'Increase Value': 'افزایش مقدار',
    'Decrease Value': 'کاهش مقدار',
};
function persianiseStepperLabels(root) {
    if (!root)
        return;
    const handles = root.querySelectorAll('[aria-label="Increase Value"], [aria-label="Decrease Value"]');
    for (const handle of handles) {
        const label = STEPPER_LABELS[handle.getAttribute('aria-label') ?? ''];
        if (label)
            handle.setAttribute('aria-label', label);
    }
}
function InputNumberInner(props, ref) {
    const ownsFormatting = props.formatter !== undefined || props.parser !== undefined;
    const node = useRef(null);
    const setRef = useCallback((value) => {
        node.current = value;
        if (typeof ref === 'function')
            ref(value);
        else if (ref)
            ref.current = value;
    }, [ref]);
    useLayoutEffect(() => {
        const input = node.current;
        persianiseStepperLabels(input?.nativeElement ?? input?.closest('.ant-input-number'));
    });
    return (_jsx(AntdInputNumber, { ...(ownsFormatting
            ? {}
            : { formatter: persianNumberFormatter, parser: persianNumberParser }), ...props, ref: setRef, controls: withRegistryStepperIcons(props.controls) }));
}
/**
 * Numeric field. Identical to AntD's except that its stepper chevrons come from
 * the registry — see {@link withRegistryStepperIcons} — and that it shows
 * Persian digits by default — see {@link persianNumberFormatter}.
 *
 * The cast preserves AntD's generic call signature, so a call site that types
 * its `onChange` as `(value: number | null) => void` keeps doing so; a
 * non-generic `forwardRef` would widen every one of them to `ValueType | null`.
 */
export const InputNumber = forwardRef(InputNumberInner);
/**
 * ── THE STAR ────────────────────────────────────────────────────────────────
 *
 * `character` is the seam, and rc-rate calls it per star with that star's own
 * `index` and the widget's current `value` (`rc-rate/es/Star.js:54`). So the
 * filled/empty distinction can be carried by the glyph itself — Bold for a
 * star the rating reaches, Linear for one it does not — which is exactly what
 * `active` means and the one place a rating widget genuinely has a toggled
 * state to report.
 *
 * That also repairs a real legibility defect rather than only swapping an icon
 * set. AntD draws every star as the same filled glyph and separates them by
 * colour alone, with `starBg` for the empty ones; measured against the page
 * that pair was **1.06:1 in light and 1.09:1 in dark** — a three-star review
 * rendered as three coral stars floating with no visible five-star track, so
 * the denominator could not be read at all. An outlined star draws the track.
 * `starBg` is raised to `borderStrong` in `packages/design/src/antd-theme.ts`
 * (the same hairline a checkbox's border uses) so the outline is actually
 * visible; both halves of the fix are asserted in `packages/design`'s
 * `a11y.test.ts`.
 *
 * `size={16}` matches the theme's `Rate.starSize`, which was moved onto the
 * icon ladder for this — an `<Icon>` writes `width`/`height` attributes, so it
 * cannot take AntD's off-ladder 18.
 */
function registryStar({ index, value }) {
    const reached = (value ?? 0) >= (index ?? 0) + 1;
    return _jsx(Icon, { name: "star", size: 16, active: reached });
}
/** Star rating drawn from the registry, filled stars in Bold. */
export const Rate = forwardRef(function Rate({ character, ...rest }, ref) {
    return (_jsx(AntdRate, { ...rest, 
        // The ref type is rc-rate's `{ focus, blur }` handle; `HTMLElement` is
        // structurally wrong for it but nothing in either panel takes a Rate ref,
        // and widening it here would leak `rc-rate` into the emitted `.d.ts`.
        ref: ref, character: character === undefined ? registryStar : character }));
});
/**
 * ── THE PROGRESS TERMINAL GLYPH ─────────────────────────────────────────────
 *
 * `format` is the seam. AntD's own branch is:
 *
 * ```js
 * if (infoPosition === 'inner' || format || (status !== 'exception' && status !== 'success'))
 *   text = (format || (n => `${n}%`))(percent, successPercent);
 * else if (status === 'exception') text = <CloseCircleFilled/> | <CloseOutlined/>;
 * else if (status === 'success')   text = <CheckCircleFilled/> | <CheckOutlined/>;
 * ```
 *
 * so supplying a `format` moves the component onto the first branch. This
 * wrapper supplies one ONLY in the cases that would otherwise reach an AntD
 * glyph, which keeps `«۴۲٪»` exactly where AntD puts it and replaces only the
 * two terminal marks. `status` is re-derived the way AntD derives it, because
 * a bar at `percent={100}` with no `status` is a success bar and draws the tick
 * just the same.
 *
 * Linear, not `active`: a finished bar is a completed state, not a *toggled*
 * one, and `active` is a claim about the latter. It reads as the circled tick
 * AntD drew, one stroke weight lighter.
 */
const ANTD_PROGRESS_STATUSES = ['normal', 'exception', 'active', 'success'];
function resolvedProgressStatus(props) {
    const successPercent = props.success?.percent;
    const shown = successPercent === undefined ? (props.percent ?? 0) : successPercent;
    const declared = props.status;
    if (declared !== undefined && ANTD_PROGRESS_STATUSES.includes(declared)) {
        return declared;
    }
    return shown >= 100 ? 'success' : 'normal';
}
function withRegistryProgressGlyph(props) {
    if (props.format !== undefined || props.showInfo === false)
        return props;
    if (props.percentPosition?.type === 'inner')
        return props;
    const status = resolvedProgressStatus(props);
    // AntD's own default is `${percent}%` — «60%», Latin digits and a Latin sign
    // on the owner's first setup screen. Persian digits and «٪», isolated so the
    // sign stays on the number's end inside an RTL row.
    if (status !== 'success' && status !== 'exception') {
        return {
            ...props,
            format: (percent) => _jsx("bdi", { children: `${toPersianDigits(Math.round(percent ?? 0))}٪` }),
        };
    }
    const name = status === 'success' ? 'check' : 'close';
    const size = (props.type ?? 'line') === 'line' ? 16 : 20;
    return { ...props, format: () => _jsx(Icon, { name: name, size: size }) };
}
/** Progress bar whose success / exception mark comes from the registry. */
export const Progress = forwardRef(function Progress(props, ref) {
    return _jsx(AntdProgress, { ...withRegistryProgressGlyph(props), ref: ref });
});
/**
 * ── THE SWITCH'S SPINNER — THE ONE WITH NO PROP ─────────────────────────────
 *
 * `Switch` is the exception in this file, and it is worth being precise about
 * why rather than adding it to a growing tolerance list. AntD builds the handle
 * itself and hands it to `rc-switch` as `loadingIcon`:
 *
 * ```js
 * const loadingIcon = <div className={`${prefixCls}-handle`}>
 *   {loading && <LoadingOutlined className={`${prefixCls}-loading-icon`} />}
 * </div>;
 * …
 * <RcSwitch {...restProps} … loadingIcon={loadingIcon} />
 * ```
 *
 * `loadingIcon` is assigned AFTER `restProps` is spread, so passing one through
 * is overwritten; `ConfigProvider` has no switch-icon slot; and hiding the
 * glyph in CSS would leave the `.anticon` node in the DOM, which is the thing
 * being counted and the thing that ships.
 *
 * So the seam is `loading` itself. The wrapper keeps every behaviour AntD's
 * `loading` has — the control is disabled, the track dims through
 * `.ant-switch-loading` — and takes over only the drawing: no `loading` reaches
 * AntD, so no glyph is built, and `.bk-switch-loading` in `styles/ui.css`
 * draws the spinner as `.ant-switch-handle::after`. That pseudo-element is free
 * (AntD uses `::before` for the knob) and, because it hangs off the handle, it
 * inherits the handle's size and travels with it when the switch is checked —
 * no geometry is restated here.
 *
 * `aria-busy` is added because it was never there: AntD's `loading` communicates
 * only through a spinner and a disabled attribute, and «در حال ذخیره» is not
 * something a screen-reader user could infer from either.
 */
export const Switch = forwardRef(function Switch({ loading, disabled, className, ...rest }, ref) {
    return (_jsx(AntdSwitch, { ...rest, ref: ref, disabled: disabled || loading, ...(loading ? { 'aria-busy': true } : {}), className: loading ? ['bk-switch-loading', className].filter(Boolean).join(' ') : className }));
});
/**
 * ── SEGMENTED'S ENGLISH NAME ────────────────────────────────────────────────
 *
 * rc-segmented names its radiogroup `aria-label="segmented control"` unless
 * the call site passes one, so the calendar's «روز / هفته» switch was
 * announced in English. A call site should still name its own control («نمای
 * تقویم»); this only makes sure the fallback is Persian.
 */
export function Segmented(props) {
    return _jsx(AntdSegmented, { "aria-label": "\u06AF\u0632\u06CC\u0646\u0647\u200C\u0647\u0627", ...props });
}
//# sourceMappingURL=data-entry.js.map